"use client";
import { T, useTextLocale, tl } from "@/components/t";

import { useEffect } from "react";
import Link from "next/link";
import { AlertTriangle, RefreshCw, Plus, FileSearch, Target } from "lucide-react";

export default function CVError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error("[careerkit] /cv error:", error);
  }, [error]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="rounded-2xl border bg-red-500/[0.04] border-red-500/20 p-5 mb-8 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-foreground"><T de={"CV-Bereich momentan nicht verfügbar."} en={"The CV area is unavailable right now."} /></p>
          <p className="text-muted-foreground mt-0.5">
            <T de={"Deine gespeicherten Lebensläufe sind sicher, wir konnten die Liste nur gerade nicht laden."} en={"Your saved CVs are safe, we just couldn't load the list."} />
          </p>
          <button
            onClick={reset}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90"
          >
            <RefreshCw className="h-3 w-3" /> <T de="Erneut versuchen" en="Try again" />
          </button>
        </div>
      </div>

      <h1 className="text-2xl font-extrabold text-foreground tracking-tight mb-1"><T de={"Lebenslauf-Tools"} en={"CV tools"} /></h1>
      <p className="text-sm text-muted-foreground mb-6">
        <T de={"Solange dein CV-Dashboard nicht lädt, kannst du diese Tools direkt nutzen:"} en={"While your CV dashboard isn't loading, you can use these tools directly:"} />
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link
          href="/cv/new"
          className="rounded-2xl border bg-card p-5 hover:border-primary/30 hover:bg-muted/40 transition-colors"
        >
          <Plus className="h-5 w-5 text-primary mb-2" />
          <p className="font-bold text-foreground mb-1">Neuen CV anlegen</p>
          <p className="text-xs text-muted-foreground"><T de={"Mit einer der vier Vorlagen starten"} en={"Start with one of the four templates"} /></p>
        </Link>
        <Link
          href="/cv-review"
          className="rounded-2xl border bg-card p-5 hover:border-primary/30 hover:bg-muted/40 transition-colors"
        >
          <FileSearch className="h-5 w-5 text-primary mb-2" />
          <p className="font-bold text-foreground mb-1"><T de="CV-Review" en="Resume Review" /></p>
          <p className="text-xs text-muted-foreground">ATS-Score & Inhalts-Feedback</p>
        </Link>
        <Link
          href="/apply"
          className="rounded-2xl border bg-card p-5 hover:border-primary/30 hover:bg-muted/40 transition-colors"
        >
          <Target className="h-5 w-5 text-primary mb-2" />
          <p className="font-bold text-foreground mb-1">Bewerbungs-Hub</p>
          <p className="text-xs text-muted-foreground">Anschreiben + Tracker</p>
        </Link>
      </div>

      {error.digest && (
        <p className="text-xs text-muted-foreground text-center mt-6">
          <T de={"Fehler-ID:"} en={"Error ID:"} /> <code className="rounded bg-muted px-1 py-0.5">{error.digest}</code>
        </p>
      )}
    </div>
  );
}
